import { useState, useEffect } from "react";
import { useSnackbar } from 'notistack';
// @mui
import {
  Card,
  Box,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
// hooks
import useAuth from '../../hooks/useAuth';
import useCompany from '../../hooks/useCompany';
import useResponsive from '../../hooks/useResponsive';
// components
import SvgIconStyle from '../../components/SvgIconStyle';
import {
  TimeOffConfirmDialog,
  FirstOnDialog,
  TimeOnDialog,
} from './';
import { EmbedOnboardingDialog } from '../../sections/onboarding';
// utils
import axios from "../../utils/axios";
import { fKrTrackingTimeShortKST } from "../../utils/formatTime";


export default function TimeTracker ({ companyId, currentData, reloadData, isFirstOn=false, coreTime=null }) {
  const { user, getUser } = useAuth();
  const { company } = useCompany();
  const { enqueueSnackbar } = useSnackbar();
  const companyName = company.find(c => c.id === parseInt(companyId))?.name;
  const [isLoading, setIsLoading] = useState(false);
  const [isOnDialogOpen, setOnDialogOpen] = useState(false);
  const [isOffConfirmDialogOpen, setIsOffConfirmDialogOpen] = useState(false);
  const [isOnboardingDialogOpen, setIsOnboardingDialogOpen] = useState(false);
  const [isFirstOnDialogOpen, setIsFirstOnDialogOpen] = useState(false);
  const smUp = useResponsive('up', 'sm');

  const pastTime = currentData?.start_at ? new Date().getTime() - new Date(currentData?.start_at).getTime() : 0;
  const hours = Math.max(Math.floor(pastTime / (1000 * 60 * 60)), 0);
  const minutes = Math.max(Math.floor((pastTime % (1000 * 60 * 60)) / (1000 * 60)), 0);

  const closeFirstOnDialog = () => {
    setIsFirstOnDialogOpen(false);
    setOnDialogOpen(true);
  };

  const trackingOnClicked = async () => {
    // 출근 다이얼로그 바로 열기
    if (isFirstOn) {
      setIsFirstOnDialogOpen(true);
    } else {
      setOnDialogOpen(true);
    }
  };

  const trackingOn = async (data) => {
    try {
      setIsLoading(true);
      await axios.post(`/api/works/time-tracking/crew/company/${companyId}/on/`, data);
      setIsLoading(false);
      enqueueSnackbar('근무가 시작되었습니다.', { variant: 'success' });
      reloadData();
    } catch (err) {
      setIsLoading(false);
      if (err.response && err.response.status === 400) {
        enqueueSnackbar(err.response.data.message, { variant: 'warning' });
      }
      reloadData();
      console.error(err);
    }
  };

  const trackingOff = async (offData) => {
    try {
      setIsLoading(true);
      const timeTrackingItemId = offData.time_tracking_item_id;
      const postData = {
        end_at: offData.end_at,
        modified_start_at: offData.modified_start_at,
        modified_end_at: offData.modified_end_at,
        dones: [...offData.dones],
        undones: [...offData.undones],
        removes: [...offData.removes],
        next_work_schedule: offData.next_work_schedule,
      }
      await axios.post(`/api/works/time-tracking/crew/company/${companyId}/${timeTrackingItemId}/off/`, postData);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      if (err.response && err.response.status === 400) {
        enqueueSnackbar(err.response.data.message, { variant: 'warning' });
      }
      console.error(err);
    }
  };

  const onboard = async () => {
    setIsOnboardingDialogOpen(true);
    await axios.post(`/api/accounts/onboard/crew/`,);
    getUser();
  }

  const openTrackingOffConfirm = async () => {
    setIsOffConfirmDialogOpen(true);
  };

  const closeTrackingOffConfirm = () => {
    setIsOffConfirmDialogOpen(false);
    reloadData();
  };

  useEffect(() => {
    if (!user?.crew?.onboarded_at) {
      onboard();
    }
  }, [user]);

  return (
    <Card sx={{ height: '100%', maxHeight: 200, display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          px: { xs: 3, sm: 5},
          py: { xs: 3, sm: 4},
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {!currentData.start_at && (
          <Stack spacing={2} sx={{ height: '100%' }}>
            <Typography variant="h6">
              업무를 시작하실건가요?
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              <b>출근하기</b>를 눌러 {companyName && <><b style={{ display: 'inline-block', maxWidth: '40%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>{companyName}</b> 회사의 </>}오늘 근무를 기록해주세요.
            </Typography>
            <Box>
              <LoadingButton
                loading={isLoading}
                onClick={trackingOnClicked}
                variant="contained"
                size="large"
                sx={{ px: smUp ? 4 : 0 }}
                fullWidth={!smUp}
                startIcon={<SvgIconStyle src={'/icons/icon-play-mono.svg'} sx={{ width: 16, height: 16 }} />}
              >
                출근하기
              </LoadingButton>
            </Box>
          </Stack>
        )}
        {currentData.start_at && !currentData.end_at && (
          <Stack spacing={2} sx={{ height: '100%', justifyContent: 'space-between' }}>
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ width: 20, height: 20, color: "secondary.main" }} />
                <Typography variant="subtitle1" sx={{ color: "secondary.main" }}>
                  {hours > 0 ? `${hours}시간` : ""} {minutes}분 근무중
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {fKrTrackingTimeShortKST(currentData.start_at)} ~
              </Typography>
            </Stack>
            <Box>
              <LoadingButton
                loading={isLoading}
                onClick={openTrackingOffConfirm}
                variant="contained"
                size="large"
                sx={{ px: smUp ? 4 : 0 }}
                fullWidth={!smUp}
                disableRipple
                startIcon={<SvgIconStyle src={'/icons/ic_door.svg'} sx={{ width: 16, height: 16 }} />}
              >
                퇴근하기
              </LoadingButton>
            </Box>
          </Stack>
        )}
      </Box>
      <TimeOnDialog
        timeTrackingData={currentData}
        isOpen={isOnDialogOpen}
        closeDialog={() => setOnDialogOpen(false)}
        trackingOn={trackingOn}
      />
      <TimeOffConfirmDialog
        timeTrackingData={currentData}
        isOpen={isOffConfirmDialogOpen}
        closeDialog={closeTrackingOffConfirm}
        trackingOff={trackingOff}
        onCompanyId={companyId}
        coreTime={coreTime}
      />
      <EmbedOnboardingDialog
        isOpen={isOnboardingDialogOpen}
        closeDialog={() => setIsOnboardingDialogOpen(false)}
      />
      <FirstOnDialog
        isOpen={isFirstOnDialogOpen}
        closeDialog={closeFirstOnDialog}
      />
    </Card>
  )
}
